import { useState } from "react";
import { useChartFormat } from "./chartLocale";
import { ChartLegend } from "./ChartLegend";
import { ChartTooltip } from "./ChartTooltip";
import { ChartFrame, EmptyChart, GridLines, YAxisLabels } from "./chartKit";
import { niceTicks } from "./chartMath";

export type LineSeries = {
  name: string;
  color: string;
  /** `null` = no data in that bucket: the line breaks there (a gap, not a zero). */
  values: (number | null)[];
};

export type LineChartProps = {
  /** Shared x-axis labels (buckets), same length as each series' `values`. */
  labels: string[];
  series: LineSeries[];
  /** Fill the area under each line. */
  area?: boolean;
  height?: number;
  emptyLabel?: string;
  testId?: string;
  formatValue?: (v: number) => string;
};

/**
 * Hand-rolled multi-series line chart (no charting dependency).
 *
 * Used for time-bucketed reports (ticket volume, backlog trend). One y axis
 * starting at zero, 2 px lines, a crosshair + tooltip per bucket and a legend
 * for two or more series. A single bucket gets a persistent marker per series.
 * Negative values extend the axis below zero. Hit rects are programmatically focusable but out of
 * the tab order (dense); each keeps role="img" and an aria-label with the values.
 */
export function LineChart({
  labels,
  series,
  area = false,
  height = 160,
  emptyLabel,
  testId,
  formatValue,
}: LineChartProps) {
  const { t, number } = useChartFormat();
  const fmt = formatValue ?? number;
  const [hover, setHover] = useState<number | null>(null);
  const points = labels.length;
  if (points === 0 || series.every((s) => s.values.length === 0)) {
    return <EmptyChart height={height} label={emptyLabel ?? t("reports.chart.noData")} testId={testId} />;
  }

  const allValues = series.flatMap((s) => s.values).filter((v): v is number => v !== null);
  const lowest = Math.min(0, ...allValues);
  // Negative values get their own gridlines below zero.
  const negTicks = lowest < 0 ? niceTicks(-lowest).slice(1).map((v) => -v).reverse() : [];
  const ticks = [...negTicks, ...niceTicks(Math.max(1, ...allValues))];
  const dataMin = ticks[0];
  const max = ticks[ticks.length - 1];
  const range = max - dataMin || 1;
  const chartHeight = height - 40;
  const yOf = (v: number) => chartHeight - 2 - ((v - dataMin) / range) * (chartHeight - 6);
  const stepX = points > 1 ? 100 / (points - 1) : 0;
  const xOf = (i: number) => (points > 1 ? i * stepX : 50);

  /** Runs of consecutive buckets that have data: [first, last] index pairs. */
  const runsOf = (values: (number | null)[]) => {
    const runs: [number, number][] = [];
    values.forEach((v, i) => {
      if (v === null || v === undefined) return;
      const last = runs[runs.length - 1];
      if (last && last[1] === i - 1) last[1] = i;
      else runs.push([i, i]);
    });
    return runs;
  };
  const runPoints = (values: (number | null)[], [from, to]: [number, number]) =>
    Array.from({ length: to - from + 1 }, (_, k) => `${xOf(from + k)},${yOf(values[from + k] as number)}`);
  // One subpath per run; a lone bucket becomes a zero-length stroke that the round cap draws as a dot.
  const linePath = (values: (number | null)[]) =>
    runsOf(values)
      .map((run) => {
        const pts = runPoints(values, run);
        return `M${pts[0]}${pts.length === 1 ? `L${pts[0]}` : `L${pts.slice(1).join("L")}`}`;
      })
      .join("");
  const areaPath = (values: (number | null)[]) =>
    runsOf(values)
      .map((run) => `M${xOf(run[0])},${yOf(0)}L${runPoints(values, run).join("L")}L${xOf(run[1])},${yOf(0)}Z`)
      .join("");
  const labelCount = Math.min(points, 5);
  const labelAt = (k: number) => (labelCount > 1 ? Math.round((k * (points - 1)) / (labelCount - 1)) : 0);

  return (
    <ChartFrame
      testId={testId}
      onLeave={() => setHover(null)}
      axis={<YAxisLabels ticks={ticks} yOf={yOf} format={fmt} />}
      footer={
        <>
          <div className="relative ml-9 mt-2 h-4 text-[10px] text-muted" data-testid={testId ? `${testId}-xlabels` : undefined}>
            {Array.from({ length: labelCount }, (_, k) => {
              const at = labelAt(k);
              const last = k === labelCount - 1 && labelCount > 1;
              return (
                <span
                  key={at}
                  className={`absolute top-0 whitespace-nowrap ${labelCount === 5 && k % 2 === 1 ? "hidden sm:block" : ""}`}
                  style={{ left: `${xOf(at)}%`, transform: k === 0 && labelCount > 1 ? undefined : last ? "translateX(-100%)" : "translateX(-50%)" }}
                >
                  {labels[at]}
                </span>
              );
            })}
          </div>
          {series.length >= 2 && (
            <ChartLegend
              testId={testId ? `${testId}-legend` : undefined}
              items={series.map((s) => ({ key: s.name, label: s.name, color: s.color }))}
            />
          )}
        </>
      }
    >
      <svg
        viewBox={`0 0 100 ${chartHeight}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height: chartHeight }}
        role="group"
        aria-label={`${series.map((s) => s.name).join(", ")}: ${labels[0]}${points > 1 ? ` – ${labels[points - 1]}` : ""}`}
      >
        <GridLines ticks={ticks} yOf={yOf} />
        {area &&
          series.map((s) =>
            runsOf(s.values).length > 0 ? (
              <path
                key={`a-${s.name}`}
                d={areaPath(s.values)}
                fill={s.color}
                fillOpacity={0.15}
                stroke="none"
                data-testid={testId ? `${testId}-area-${s.name}` : undefined}
              />
            ) : null,
          )}
        {series.map((s) => (
          <path
            key={s.name}
            d={linePath(s.values)}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            data-testid={testId ? `${testId}-series-${s.name}` : undefined}
          />
        ))}
        {hover !== null && (
          <line
            x1={xOf(hover)}
            x2={xOf(hover)}
            y1={0}
            y2={chartHeight}
            stroke="var(--color-muted)"
            strokeWidth={1}
            strokeDasharray="3 3"
            vectorEffect="non-scaling-stroke"
            data-testid={testId ? `${testId}-crosshair` : undefined}
          />
        )}
        {labels.map((label, i) => {
          const w = points > 1 ? stepX : 100;
          const x = points > 1 ? Math.max(0, xOf(i) - w / 2) : 0;
          const edge = points > 1 && (i === 0 || i === points - 1);
          return (
            <rect
              key={`h-${i}`}
              x={x}
              y={0}
              width={edge ? w / 2 : w}
              height={chartHeight}
              fill="transparent"
              tabIndex={-1}
              role="img"
              aria-label={`${label}: ${series.map((s) => `${s.name} ${s.values[i] == null ? "—" : fmt(s.values[i] as number)}`).join(", ")}`}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              onMouseEnter={() => setHover(i)}
              onMouseMove={() => setHover(i)}
              data-testid={testId ? `${testId}-hit-${i}` : undefined}
            >
              <title>{label}</title>
            </rect>
          );
        })}
      </svg>
      {(hover !== null || points === 1) &&
        series.map((s) =>
          s.values[hover ?? 0] == null ? null : (
            <span
              key={`m-${s.name}`}
              data-testid={testId ? `${testId}-marker-${s.name}` : undefined}
              className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface"
              style={{ left: `${xOf(hover ?? 0)}%`, top: yOf(s.values[hover ?? 0] as number), backgroundColor: s.color }}
              aria-hidden
            />
          ),
        )}
      {hover !== null && (
        <ChartTooltip
          testId={testId ? `${testId}-tooltip` : undefined}
          x={xOf(hover)}
          y={Math.min(...series.map((s) => (s.values[hover] == null ? chartHeight : yOf(s.values[hover] as number)))) - 8}
          title={labels[hover]}
          rows={series.map((s) => ({
            label: s.name,
            value: s.values[hover] == null ? "—" : fmt(s.values[hover] as number),
            color: s.color,
          }))}
        />
      )}
    </ChartFrame>
  );
}
